import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, catchError, filter, takeUntil, throwError } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { UserContextService } from '../auth/user-context.service';
import { API_CONFIG } from './api.config';
import { LocalizationService } from '../i18n/localization.service';

let lastStatusMessage = '';
let lastStatusMessageAt = 0;
const expiredSession = new Subject<string>();

export const apiAuthInterceptor: HttpInterceptorFn = (request, next) => {
  const auth = inject(AuthService);
  const userContext = inject(UserContextService);
  const router = inject(Router);
  const apiConfig = inject(API_CONFIG);
  const snackBar = inject(MatSnackBar);
  const localization = inject(LocalizationService);
  const isOAuthRequest = request.url.includes('/oauth/');
  const isDelegatedOrganisationTokenRequest = /\/organisations\/[^/]+\/token$/.test(request.url);
  const isPublicOAuthRequest =
    request.url.includes('/oauth/token') ||
    request.url.includes('/oauth/invitations/accept') ||
    request.url.includes('/oauth/password-reset/request') ||
    request.url.includes('/oauth/password-reset/accept');
  const adminConsoleRequest =
    (router.url === '/admin' || (router.url.startsWith('/admin/') && !router.url.startsWith('/admin/users'))) &&
    !auth.isDelegatedOrganisationSession();
  const organisationId = adminConsoleRequest ? 0 : userContext.currentOrganisationId();
  const headers: Record<string, string> = {};

  if (!isOAuthRequest && !isDelegatedOrganisationTokenRequest && !request.headers.has('X-Organisation-Id')) {
    headers['x-organisation-id'] = `${organisationId}`;
  }

  const token = auth.accessToken();
  if (token && !request.headers.has('Authorization') && !isPublicOAuthRequest) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const isApiGet = request.method === 'GET' && request.url.startsWith(apiConfig.baseUrl.replace(/\/$/, ''));
  const apiRequest = request.clone({
    setHeaders: isApiGet
      ? { ...headers, 'Cache-Control': 'no-store', Pragma: 'no-cache' }
      : headers,
    params: isApiGet ? request.params.set('_ts', `${Date.now()}`) : request.params
  });

  const cancelWhenSessionExpires$ = expiredSession.pipe(filter((expiredToken) => expiredToken === token));

  return next(apiRequest).pipe(
    takeUntil(cancelWhenSessionExpires$),
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {
        if (error.status === 401) {
          if (token) {
            expiredSession.next(token);
          }
          auth.clearSession();
          userContext.clearAuthenticatedUser();
          showStatusMessage(snackBar, localization.translate('api.error.authenticationRequired'));
          if (!router.url.startsWith('/login')) {
            void router.navigate(['/login']);
          }
        } else if (error.status === 403) {
          showStatusMessage(snackBar, localization.translate('api.error.accessDenied'));
        }
      }
      return throwError(() => error);
    })
  );
};

function showStatusMessage(snackBar: MatSnackBar, message: string): void {
  const now = Date.now();
  if (message === lastStatusMessage && now - lastStatusMessageAt < 1500) {
    return;
  }
  lastStatusMessage = message;
  lastStatusMessageAt = now;
  snackBar.open(message, 'OK', {
    duration: 10000,
    verticalPosition: 'top',
    panelClass: ['app-snackbar-error']
  });
}
